import { isAdExcludedPath } from '@/utils/urlPrivacy';

/**
 * Ad-excluded routes (teen/student guides, printables) are only ever entered
 * with a full document load. Ad pixels are never initialized on them:
 * MarketingScripts decides once, from the landing URL, and leaves them out.
 *
 * Because every entry is a fresh document, no pixel loaded on an earlier page
 * of the SPA session is ever running on these routes, so there is nothing to
 * pause or resume. This guard converts any in-app navigation into one of
 * these routes (link click, navigate(), redirect, Back/Forward) into a
 * full page load before the router renders it.
 */

export type HardNavigator = {
  assign: (href: string) => void;
  replace: (href: string) => void;
  reload: () => void;
};

const browserNavigator: HardNavigator = {
  assign: (href) => window.location.assign(href),
  replace: (href) => window.location.replace(href),
  reload: () => window.location.reload(),
};

let installed = false;
let navigator: HardNavigator = browserNavigator;

function pathnameOf(href: string): string {
  return new URL(href, window.location.href).pathname.replace(/\/+$/, '') || '/';
}

/** True when moving from the current document to `targetHref` must be a full page load. */
export function needsHardNavigation(targetHref: string): boolean {
  if (typeof window === 'undefined') return false;
  let targetPath: string;
  try {
    targetPath = pathnameOf(targetHref);
  } catch {
    return false;
  }
  if (!isAdExcludedPath(targetPath)) return false;
  // Same route (hash, query or scroll-state updates): already a fresh document.
  return targetPath !== pathnameOf(window.location.href);
}

export function installAdExcludedPageGuard(nav: HardNavigator = browserNavigator): void {
  navigator = nav;
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Remember which route this document was loaded on, so Back/Forward into an
  // excluded route from another route reloads instead of rendering in place.
  let documentPath = pathnameOf(window.location.href);

  const wrap = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method];
    window.history[method] = function guardedHistoryChange(
      this: History,
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) {
      if (url != null) {
        const targetHref = new URL(String(url), window.location.href).href;
        if (needsHardNavigation(targetHref)) {
          if (method === 'replaceState') navigator.replace(targetHref);
          else navigator.assign(targetHref);
          return undefined;
        }
      }
      return original.call(this, data, unused, url);
    } as History[typeof method];
  };
  wrap('pushState');
  wrap('replaceState');

  // Capture phase on window, registered at boot: runs before the router's
  // listener, and stops it so the excluded route never renders in this document.
  window.addEventListener(
    'popstate',
    (event) => {
      const currentPath = pathnameOf(window.location.href);
      if (isAdExcludedPath(currentPath) && currentPath !== documentPath) {
        event.stopImmediatePropagation();
        navigator.reload();
        return;
      }
      documentPath = currentPath;
    },
    true
  );
  const trackDocumentPath = () => {
    documentPath = pathnameOf(window.location.href);
  };
  for (const method of ['pushState', 'replaceState'] as const) {
    const guarded = window.history[method];
    window.history[method] = function trackPath(this: History, ...args: Parameters<History['pushState']>) {
      const result = guarded.apply(this, args);
      trackDocumentPath();
      return result;
    } as History[typeof method];
  }
}

export function resetAdExcludedPageGuardForTests(): void {
  navigator = browserNavigator;
}
